import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { setConsent } from "../lib/consent";

const LAST_UPDATED = "August 22, 2026";

export default function PrivacyPolicy() {
  const { t } = useTranslation("privacy");
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="mx-auto max-w-3xl px-6 py-12 sm:py-16">
        <Link
          to="/"
          className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] hover:text-[#FF5500]"
        >
          ← {t("nav.back", { brand: "TrapMan" })}
        </Link>

        <header className="mt-8 mb-12">
          <h1 className="font-clash text-4xl font-bold sm:text-5xl">
            {t("header.title")}
          </h1>
          <p className="mt-3 font-mono text-xs uppercase tracking-wider text-[#A1A1AA]">
            {t("header.lastUpdated")}: {LAST_UPDATED}
          </p>
        </header>

        <div className="space-y-8 font-body leading-relaxed text-[#D4D4D8]">
          <section>
            <p>{t("intro.body")}</p>
          </section>

          <Section title={t("responsible.heading")}>
            <p>
              {t("responsible.bodyBefore")}{" "}
              <a
                href="mailto:support@voleor.com"
                className="text-[#FF5500] hover:text-[#FF7733]"
              >
                support@voleor.com
              </a>
              {t("responsible.bodyAfter")}
            </p>
          </Section>

          <Section title={t("collect.heading")}>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                <strong className="text-white">{t("collect.accountLabel")}</strong>{" "}
                {t("collect.accountBody")}
              </li>
              <li>
                <strong className="text-white">{t("collect.authLabel")}</strong>{" "}
                {t("collect.authBody")}
              </li>
              <li>
                <strong className="text-white">{t("collect.licenceLabel")}</strong>{" "}
                {t("collect.licenceBody")}
              </li>
              <li>
                <strong className="text-white">{t("collect.billingLabel")}</strong>{" "}
                {t("collect.billingBody")}
              </li>
              <li>
                <strong className="text-white">{t("collect.supportLabel")}</strong>{" "}
                {t("collect.supportBody")}
              </li>
            </ul>
          </Section>

          <Section title={t("why.heading")}>
            <ul className="list-disc space-y-2 pl-6">
              <li>{t("why.item1")}</li>
              <li>{t("why.item2")}</li>
              <li>{t("why.item3")}</li>
              <li>{t("why.item4")}</li>
              <li>{t("why.item5")}</li>
              <li>{t("why.item6")}</li>
              <li>{t("why.item7")}</li>
            </ul>
            <p className="mt-4">
              <strong className="text-white">{t("why.noSellLabel")}</strong>{" "}
              {t("why.noSellBody")}
            </p>
          </Section>

          <Section title={t("marketing.heading")}>
            <p>{t("marketing.intro")}</p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li>
                <strong className="text-white">{t("marketing.transactionalLabel")}</strong>{" "}
                {t("marketing.transactionalBody")}
              </li>
              <li>
                <strong className="text-white">{t("marketing.marketingLabel")}</strong>{" "}
                {t("marketing.marketingBody1")}{" "}
                <em>{t("marketing.marketingEmphasis")}</em>{" "}
                {t("marketing.marketingBody2")}
              </li>
            </ul>
            <p className="mt-4">
              <strong className="text-white">{t("marketing.neverSharedLabel")}</strong>{" "}
              {t("marketing.neverSharedBody")}
            </p>
            <p className="mt-4">
              <strong className="text-white">{t("marketing.legalBasisLabel")}</strong>{" "}
              {t("marketing.legalBasisBody")}
            </p>
            <ul className="mt-2 list-disc space-y-2 pl-6">
              <li>
                <strong className="text-white">{t("marketing.consentLabel")}</strong>{" "}
                {t("marketing.consentBody")}
              </li>
              <li>
                <strong className="text-white">{t("marketing.softOptInLabel")}</strong>{" "}
                {t("marketing.softOptInBody")}
              </li>
            </ul>
            <p className="mt-4">
              <strong className="text-white">{t("marketing.optOutLabel")}</strong>{" "}
              {t("marketing.optOutBody1")}{" "}
              <Link to="/dashboard/settings" className="text-[#FF5500] hover:text-[#FF7733]">
                {t("marketing.accountSettingsLink")}
              </Link>
              {t("marketing.optOutBody2")}{" "}
              <a href="mailto:support@voleor.com" className="text-[#FF5500] hover:text-[#FF7733]">
                support@voleor.com
              </a>
              {t("marketing.optOutBody3")}
            </p>
            <p className="mt-4">
              <strong className="text-white">{t("marketing.retentionLabel")}</strong>{" "}
              {t("marketing.retentionBody")}
            </p>
          </Section>

          <Section title={t("processors.heading")}>
            <p>{t("processors.intro")}</p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li>
                <strong className="text-white">Stripe</strong>{" "}
                {t("processors.stripe")}
              </li>
              <li>
                <strong className="text-white">OVHcloud</strong>{" "}
                {t("processors.ovh")}
              </li>
              <li>
                <strong className="text-white">Google</strong>{" "}
                {t("processors.google")}
              </li>
              <li>
                <strong className="text-white">PostHog</strong>{" "}
                {t("processors.posthog")}
              </li>
              <li>
                <strong className="text-white">Google Analytics</strong>{" "}
                {t("processors.googleAnalytics")}
              </li>
              <li>
                <strong className="text-white">Meta</strong>{" "}
                {t("processors.meta")}
              </li>
            </ul>
            <p className="mt-4">{t("processors.outro")}</p>
          </Section>

          <Section title={t("cookies.heading")}>
            <p>{t("cookies.intro")}</p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li>
                <strong className="text-white">{t("cookies.essentialLabel")}</strong>{" "}
                {t("cookies.essentialBody")}
              </li>
              <li>
                <strong className="text-white">{t("cookies.analyticsLabel")}</strong>{" "}
                {t("cookies.analyticsBody")}
              </li>
              <li>
                <strong className="text-white">{t("cookies.posthogLabel")}</strong>{" "}
                {t("cookies.posthogBody")}
              </li>
              <li>
                <strong className="text-white">{t("cookies.metaLabel")}</strong>{" "}
                {t("cookies.metaBody")}
              </li>
            </ul>
            <p className="mt-4">
              {t("cookies.withdrawBody")}{" "}
              <button
                type="button"
                onClick={() => setConsent(null)}
                className="text-[#FF5500] underline underline-offset-2 hover:text-[#FF7733]"
              >
                {t("cookies.withdrawLink")}
              </button>
              {t("cookies.withdrawSuffix")}
            </p>
          </Section>

          <Section title={t("retention.heading")}>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                <strong className="text-white">{t("retention.accountLabel")}</strong>{" "}
                {t("retention.accountBody")}
              </li>
              <li>
                <strong className="text-white">{t("retention.invoicesLabel")}</strong>{" "}
                {t("retention.invoicesBody")}
              </li>
              <li>
                <strong className="text-white">{t("retention.logsLabel")}</strong>{" "}
                {t("retention.logsBody")}
              </li>
            </ul>
          </Section>

          <Section title={t("rights.heading")}>
            <p>
              {t("rights.body1")}{" "}
              <Link to="/dashboard/settings" className="text-[#FF5500] hover:text-[#FF7733]">
                {t("rights.accountSettingsLink")}
              </Link>
              {t("rights.body2")}{" "}
              <a
                href="mailto:support@voleor.com"
                className="text-[#FF5500] hover:text-[#FF7733]"
              >
                support@voleor.com
              </a>
              {t("rights.body3")}
            </p>
          </Section>

          <Section title={t("security.heading")}>
            <p>
              {t("security.body")}{" "}
              <a
                href="mailto:support@voleor.com"
                className="text-[#FF5500] hover:text-[#FF7733]"
              >
                support@voleor.com
              </a>
              .
            </p>
          </Section>

          <Section title={t("changes.heading")}>
            <p>{t("changes.body")}</p>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-clash mb-3 text-2xl font-semibold text-white">
        {title}
      </h2>
      {children}
    </section>
  );
}
